/**
 * Scheduled messages (Slack "Schedule message", logic/scheduled): mine, for the composer bar ("1 scheduled message")
 * and the Scheduled list (per conversation or all). Scheduling comes from the composer's send menu
 * (ChannelViewModel.scheduleDraft → `schedule`); here: edit the text, reschedule, cancel, send now. Changes are
 * optimistic; a refused one is undone by refetching. Messages posted by the server at their time drop out on the next
 * refresh (and when the conversation gets their message.created). Online only.
 */
import type { Message, PersistentEvent, ScheduledMessage } from "@bundle/contracts";
import { forConversation, removeScheduled, sortScheduled, upsertScheduled } from "../logic/scheduled.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ScheduledRow = {
  id: string;
  scheduled: ScheduledMessage;
  /** "#design", a DM's people. */
  conversation: string;
  isDm: boolean;
  inThread: boolean;
};

export type ScheduledViewState = {
  status: "loading" | "ready" | "failed";
  /** All of mine, soonest first. */
  messages: ScheduledMessage[];
  /** The one being edited (text + time), absent = none. */
  editing?: { id: string; text: string; sendAt: number };
  offline: boolean;
};

export class ScheduledViewModel extends ViewModel<ScheduledViewState> {
  private inFlight: Promise<void> | undefined;
  private again = false;

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", messages: [], offline: !ws.state.network });
  }

  refresh(): Promise<void> {
    if (this.inFlight !== undefined) {
      this.again = true;
      return this.inFlight;
    }
    this.inFlight = this.ws.deps.api.listScheduled(this.ws.wsId).then(
      (messages) => {
        if (!this.isDisposed) this.update({ status: "ready", messages: sortScheduled(messages), offline: false });
      },
      () => {
        if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed", offline: !this.ws.state.network });
      },
    );
    return this.inFlight.finally(() => {
      this.inFlight = undefined;
      if (this.again) {
        this.again = false;
        void this.refresh();
      }
    });
  }

  /** A workspace event: one of my messages that was scheduled got posted → refetch. */
  onEvent(event: PersistentEvent): void {
    if (event.type === "message.created" && event.message.authorId === this.ws.state.meId && event.message.clientId?.startsWith("sched:") === true) {
      const id = event.message.clientId.slice("sched:".length);
      this.update({ messages: removeScheduled(this.state.messages, id) });
    }
  }

  /** One conversation's (or thread's) scheduled messages. */
  forConversation(channelId: string, threadRootId?: string): ScheduledMessage[] {
    return forConversation(this.state.messages, channelId, threadRootId);
  }

  /** Rows for a list: all of mine, or one conversation's. */
  rows(channelId?: string): ScheduledRow[] {
    const cache = this.ws.state.cache;
    const list = channelId === undefined ? this.state.messages : this.state.messages.filter((s) => s.channelId === channelId);
    return list.map((s) => {
      const c = cache?.channels[s.channelId];
      const isDm = c !== undefined && (c.kind === "dm" || c.kind === "group_dm");
      return { id: s.id, scheduled: s, conversation: c === undefined || cache === undefined ? "" : isDm ? channelTitle(cache, c.id) : `#${c.name}`, isDm, inThread: s.threadRootId !== undefined };
    });
  }

  /** Schedules a message (the composer's send menu). Rejects when the server refuses (the composer keeps the draft). */
  async schedule(req: { channelId: string; text: string; sendAt: number; threadRootId?: string; alsoSendToChannel?: boolean }): Promise<ScheduledMessage> {
    const s = await this.ws.deps.api.createScheduled(this.ws.wsId, req);
    this.update({ messages: upsertScheduled(this.state.messages, s) });
    return s;
  }

  startEdit(id: string): void {
    const s = this.state.messages.find((x) => x.id === id);
    if (s !== undefined) this.update({ editing: { id, text: s.text, sendAt: s.sendAt } });
  }

  setEditText(text: string): void {
    const e = this.state.editing;
    if (e !== undefined) this.update({ editing: { ...e, text } });
  }

  setEditTime(sendAt: number): void {
    const e = this.state.editing;
    if (e !== undefined) this.update({ editing: { ...e, sendAt } });
  }

  cancelEdit(): void {
    if (this.state.editing === undefined) return;
    const { editing: _e, ...rest } = this.state;
    this.setState(rest);
  }

  saveEdit(): void {
    const e = this.state.editing;
    if (e === undefined || e.text.trim() === "") return;
    this.cancelEdit();
    this.patch(e.id, { text: e.text.trim(), sendAt: e.sendAt });
  }

  reschedule(id: string, sendAt: number): void {
    this.patch(id, { sendAt });
  }

  cancel(id: string): void {
    if (!this.state.messages.some((s) => s.id === id)) return;
    this.update({ messages: removeScheduled(this.state.messages, id) });
    this.ws.deps.api.deleteScheduled(this.ws.wsId, id).catch(() => void this.refresh());
  }

  /** Posts it now; resolves with the message (undefined when refused: the list is refetched). */
  sendNow(id: string): Promise<Message | undefined> {
    this.update({ messages: removeScheduled(this.state.messages, id) });
    return this.ws.deps.api.sendScheduledNow(this.ws.wsId, id).then(
      (m) => m,
      () => {
        void this.refresh();
        return undefined;
      },
    );
  }

  private patch(id: string, p: { text?: string; sendAt?: number }): void {
    const s = this.state.messages.find((x) => x.id === id);
    if (s === undefined) return;
    const next: ScheduledMessage = { ...s, ...p, updatedAt: this.ws.deps.clock.now() };
    delete next.failed;
    this.update({ messages: upsertScheduled(this.state.messages, next) });
    this.ws.deps.api.updateScheduled(this.ws.wsId, id, p).then(
      // Unless it was cancelled or sent meanwhile.
      (saved) => {
        if (this.state.messages.some((x) => x.id === saved.id)) this.update({ messages: upsertScheduled(this.state.messages, saved) });
      },
      () => void this.refresh(),
    );
  }
}
